import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '../../helpers/AuthContext';
import './HelpArticleLink.css';

function stripMarkdown(md) {
    return (md || '')
        .replace(/```[\s\S]*?```/g, '')
        .replace(/`[^`]+`/g, '')
        .replace(/!\[.*?\]\(.*?\)/g, '')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .replace(/#{1,6}\s+/g, '')
        .replace(/[*_~]{1,3}/g, '')
        .replace(/>\s*/g, '')
        .replace(/\n+/g, ' ')
        .trim();
}

function ArticleCard({ article }) {
    const excerpt = stripMarkdown(article.markdown || '');
    const short = excerpt.length > 140 ? excerpt.slice(0, 140) + '…' : excerpt;
    return (
        <a
            className="hal-card"
            href={`/help/${article.slug}`}
            target="_blank"
            rel="noopener noreferrer"
        >
            {article.category && article.category !== 'Uncategorized' && (
                <span className="hal-card__category">{article.category}</span>
            )}
            <span className="hal-card__title">{article.title}</span>
            {short && <span className="hal-card__excerpt">{short}</span>}
        </a>
    );
}

export default function HelpArticleLink({
    slug,
    query,
    label,
    variant = 'icon', // 'icon' | 'link' | 'badge'
}) {
    const { fetch, API_URL } = useAuth();
    const [popover, setPopover] = useState(null);
    const enterTimer = useRef(null);
    const leaveTimer = useRef(null);
    const popoverRef = useRef(null);
    const cachedResult = useRef(null);
    const isFetching = useRef(false);

    useEffect(() => () => {
        clearTimeout(enterTimer.current);
        clearTimeout(leaveTimer.current);
    }, []);

    const fetchHelp = useCallback(async () => {
        if (isFetching.current) return;
        isFetching.current = true;
        setPopover({ status: 'loading' });
        try {
            const params = new URLSearchParams();
            if (slug) params.set('slug', slug);
            else if (query) params.set('q', query);
            const res = await fetch(`${API_URL}/v1/seller/help?${params}`);
            if (!res.ok) throw new Error('Failed');
            const json = await res.json();
            const result = { status: 'loaded', meta: json.meta, data: json.data };
            cachedResult.current = result;
            setPopover(result);
        } catch {
            setPopover({ status: 'error' });
        } finally {
            isFetching.current = false;
        }
    }, [fetch, API_URL, slug, query]);

    const handleEnter = useCallback(() => {
        clearTimeout(leaveTimer.current);
        if (cachedResult.current) {
            setPopover(cachedResult.current);
        } else if (!isFetching.current) {
            clearTimeout(enterTimer.current);
            enterTimer.current = setTimeout(fetchHelp, 280);
        }
    }, [fetchHelp]);

    const handleLeave = useCallback(() => {
        clearTimeout(enterTimer.current);
        leaveTimer.current = setTimeout(() => setPopover(null), 180);
    }, []);

    // Flip popover left anchor to right if it overflows the viewport
    useEffect(() => {
        if (!popoverRef.current || !popover) return;
        const rect = popoverRef.current.getBoundingClientRect();
        if (rect.right > window.innerWidth - 12) {
            popoverRef.current.style.left = 'auto';
            popoverRef.current.style.right = '0';
        } else {
            popoverRef.current.style.left = '';
            popoverRef.current.style.right = 'auto';
        }
    }, [popover]);

    const triggerHref = slug
        ? `/help/${slug}`
        : query
        ? `/help/search?q=${encodeURIComponent(query)}`
        : '/account/help';

    return (
        <span
            className={`help-article-link help-article-link--${variant}`}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
            onFocusCapture={handleEnter}
            onBlurCapture={(e) => {
                if (e.currentTarget.contains(e.relatedTarget)) return;
                handleLeave();
            }}
        >
            <a
                className="hal-trigger"
                href={triggerHref}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label || 'Help'}
            >
                {variant === 'icon' && (
                    <span className="hal-icon" aria-hidden="true">?</span>
                )}
                {variant === 'badge' && (
                    <span className="hal-badge">{label || 'Help'}</span>
                )}
                {variant === 'link' && (
                    <>
                        <span className="hal-icon" aria-hidden="true">?</span>
                        {label && <span className="hal-label">{label}</span>}
                    </>
                )}
            </a>

            {popover && (
                <span
                    className="hal-popover"
                    ref={popoverRef}
                    onMouseEnter={() => clearTimeout(leaveTimer.current)}
                    onMouseLeave={handleLeave}
                >
                    {popover.status === 'loading' && (
                        <span className="hal-popover__state">Loading…</span>
                    )}
                    {popover.status === 'error' && (
                        <span className="hal-popover__state hal-popover__state--error">
                            Couldn’t load help content
                        </span>
                    )}
                    {popover.status === 'loaded' && popover.meta?.type === 'article' && popover.data && (
                        <ArticleCard article={popover.data} />
                    )}
                    {popover.status === 'loaded' && popover.meta?.type === 'search' && (
                        <>
                            {(!popover.data || popover.data.length === 0) && (
                                <span className="hal-popover__state">No results found</span>
                            )}
                            {(popover.data || []).map(a => (
                                <ArticleCard key={a.slug || a.id} article={a} />
                            ))}
                        </>
                    )}
                    {popover.status === 'loaded' && popover.meta?.type === 'none' && (
                        <span className="hal-popover__state">No help article found</span>
                    )}
                </span>
            )}
        </span>
    );
}
